import type { DashboardId } from '@clinspace/shared';
import { Dashboard, type ViewRegion } from '../Dashboard.ts';
import type { CanvasPanel } from '../CanvasPanel.ts';
import { PALETTE, bars, list, sparkline, text, type Rect } from '../charts.ts';
import { labDetail } from '../details.ts';
import { hoveredRow } from './Enrollment.ts';

const GRADE_COLOURS = ['#9aa6b4', PALETTE.teal, PALETTE.amber, PALETTE.crimson, '#8b1020'];

/**
 * Lab Abnormality: grade 3+ subjects, Hy's law candidates, pending CS review; 3D ALT vs
 * bilirubin scatter (spheres in front of the panel); grade distribution per analyte.
 */
export class LabAbnormalityDashboard extends Dashboard {
  readonly id: DashboardId = 'LabAbnormality';

  protected override segmentLabel(segment: string): string {
    if (segment.startsWith('lab:')) {
      const l = this.view.lab(segment.slice(4));
      return l ? `${l.analyte} ${l.visitCode}` : segment;
    }
    return segment.replace(/^subject:/, '');
  }

  protected drawContent(ctx: CanvasRenderingContext2D, panel: CanvasPanel, area: Rect): void {
    const [subject, lab] = this.path;
    if (subject && lab) {
      const l = this.view.lab(lab.slice(4));
      if (l) {
        this.detail = labDetail(this.view, l);
        this.drawDetail(ctx, panel, area, this.detail);
      }
      return;
    }
    this.detail = undefined;
    const labs = this.view.labs(this.filters);
    if (subject) {
      const id = subject.slice(8);
      const mine = this.view.file.labs.filter((l) => l.subjectId === id);
      const analytes = [...new Set(mine.map((l) => l.analyte))];
      text(
        ctx,
        `${id} · lab series over visits (grade shading; select a point to open the value)`,
        area.x,
        area.y + 14,
        { size: 12, colour: PALETTE.muted, weight: 600 },
      );
      const cols = 2;
      const rowH = (area.h - 24) / Math.ceil(analytes.length / cols);
      const colW = area.w / cols;
      analytes.forEach((a, i) => {
        const series = mine
          .filter((l) => l.analyte === a)
          .sort((x, y) => this.view.visitIndex(x.visitCode) - this.view.visitIndex(y.visitCode));
        const r = {
          x: area.x + (i % cols) * colW,
          y: area.y + 24 + Math.floor(i / cols) * rowH,
          w: colW - 12,
          h: rowH - 4,
        };
        const worst = Math.max(...series.map((l) => l.grade));
        text(ctx, `${a} (${series[0]!.unit})`, r.x, r.y + 11, {
          size: 10,
          colour: worst >= 3 ? PALETTE.crimson : PALETTE.muted,
          weight: 600,
        });
        sparkline(
          ctx,
          series.map((l) => l.value),
          { x: r.x + 90, y: r.y + 2, w: r.w - 130, h: r.h - 6 },
          GRADE_COLOURS[Math.min(4, worst)],
          { low: series[0]!.low, high: series[0]!.high },
        );
        text(ctx, `${series[series.length - 1]!.value}`, r.x + r.w - 36, r.y + 11, {
          size: 10,
          colour: PALETTE.text,
        });
        const step = (r.w - 130) / Math.max(1, series.length - 1);
        series.forEach((l, j) =>
          panel.region<ViewRegion>({
            id: `lab:${i}:${j}`,
            x: r.x + 90 + j * step - step / 2,
            y: r.y,
            w: step,
            h: r.h,
            data: { kind: 'drill', segment: `lab:${l.id}` },
            tooltip: [
              `${l.analyte} ${l.value} ${l.unit} · ${l.visitCode}`,
              `grade ${l.grade}${l.clinicallySignificant ? ` · CS ${l.clinicallySignificant}` : ''}`,
              'Open the value',
            ],
          }),
        );
      });
      return;
    }
    const d = this.view.file.dashboards.labAbnormality;
    const grade3 = new Set(labs.filter((l) => l.grade >= 3).map((l) => l.subjectId));
    const pending = labs.filter((l) => l.clinicallySignificant === 'Pending').length;
    const ids = this.view.subjectIds(this.filters);
    const hys = d.hysLawSubjectIds.filter((id) => ids.has(id)).length;
    this.kpis(
      panel,
      ctx,
      [
        {
          label: 'Grade 3+ subjects',
          value: String(grade3.size),
          colour: PALETTE.crimson,
          sub: `${labs.filter((l) => l.grade >= 3).length} values`,
        },
        {
          label: "Hy's law candidates",
          value: String(hys),
          colour: hys ? PALETTE.crimson : PALETTE.teal,
          sub: 'ALT > 3× and bili > 2× ULN',
        },
        {
          label: 'Pending CS review',
          value: String(pending),
          colour: pending ? PALETTE.amber : PALETTE.teal,
        },
        { label: 'Lab values', value: String(labs.length) },
      ],
      { x: area.x, y: area.y, w: area.w, h: 76 },
    );
    const left: Rect = { x: area.x, y: area.y + 88, w: area.w * 0.5, h: area.h - 88 };
    text(
      ctx,
      'ALT vs bilirubin (×ULN): the scatter cube stands on the floor in front',
      left.x,
      left.y + 14,
      { size: 12, colour: PALETTE.muted, weight: 600 },
    );
    text(
      ctx,
      "x: ALT 0–5×   y: bilirubin 0–3×   red: Hy's law   amber: elevated",
      left.x,
      left.y + 32,
      { size: 10, colour: PALETTE.muted },
    );
    // Subjects ranked by their worst value: Hy's law first, then grade, then ULN multiple.
    const worst = new Map<
      string,
      {
        subjectId: string;
        grade: number;
        analyte: string;
        uln: number;
        visitCode: string;
        hysLaw: boolean;
      }
    >();
    for (const l of labs) {
      const w = worst.get(l.subjectId);
      const uln = l.ulnMultiple ?? 0;
      if (!w || l.grade > w.grade || (l.grade === w.grade && uln > w.uln))
        worst.set(l.subjectId, {
          subjectId: l.subjectId,
          grade: l.grade,
          analyte: l.analyte,
          uln,
          visitCode: l.visitCode,
          hysLaw: d.hysLawSubjectIds.includes(l.subjectId),
        });
    }
    const ranked = [...worst.values()]
      .filter((w) => w.grade >= 1 || w.hysLaw)
      .sort((a, b) => Number(b.hysLaw) - Number(a.hysLaw) || b.grade - a.grade || b.uln - a.uln)
      .slice(0, 14);
    const rows = list(
      ctx,
      ['Subject', 'Worst', 'Grade', 'Visit', 'Flag'],
      ranked.map((s) => ({
        cells: [
          s.subjectId,
          `${s.analyte}${s.uln ? ` ${s.uln}× ULN` : ''}`,
          String(s.grade),
          s.visitCode,
          s.hysLaw ? "Hy's law" : s.grade >= 3 ? 'grade 3+' : 'abnormal',
        ],
        flag: s.hysLaw || s.grade >= 3 ? 'x' : undefined,
        colour: s.hysLaw ? PALETTE.crimson : PALETTE.amber,
      })),
      { x: left.x, y: left.y + 40, w: left.w, h: left.h - 40 },
      {
        title: 'Subjects by worst lab value',
        widths: [0.24, 0.3, 0.12, 0.14, 0.2],
        hoveredIndex: hoveredRow(panel, 'row:'),
        rowH: 18,
        emptyText: 'No abnormal values for this filter',
      },
    );
    rows.forEach((r, i) =>
      panel.region<ViewRegion>({
        id: `row:${i}`,
        ...r,
        data: { kind: 'drill', segment: `subject:${ranked[i]!.subjectId}` },
        tooltip: [ranked[i]!.subjectId, 'Open the lab series'],
      }),
    );
    const right: Rect = {
      x: area.x + area.w * 0.54,
      y: area.y + 88,
      w: area.w * 0.46,
      h: area.h - 88,
    };
    const analytes = [...new Set(labs.map((l) => l.analyte))];
    const data = analytes.map((a) => {
      const mine = labs.filter((l) => l.analyte === a);
      return {
        label: a,
        values: [1, 2, 3, 4].map((g) => mine.filter((l) => l.grade === g).length),
        colours: [PALETTE.teal, PALETTE.amber, PALETTE.crimson, '#8b1020'],
      };
    });
    const rects = bars(ctx, data, right, {
      stacked: true,
      title: 'Abnormal values by grade (1 / 2 / 3 / 4) per analyte',
      hoveredIndex: hoveredRow(panel, 'analyte:'),
      labelSize: 8,
    });
    rects.forEach((r, i) =>
      panel.region<ViewRegion>({
        id: `analyte:${i}`,
        ...r,
        data: { kind: 'info' },
        tooltip: [
          analytes[i]!,
          `grade 1: ${data[i]!.values[0]} · 2: ${data[i]!.values[1]} · 3: ${data[i]!.values[2]} · 4: ${data[i]!.values[3]}`,
        ],
        selectable: false,
      }),
    );
  }
}
